import axios from 'axios';
import resultsTemplate from './mobileSearch.template.hbs';

/**
 * Mobile search
 * Javascript functionality for a (mobile) search.
 * @version 3.0.0
 * @exports MobileSearch
 */
export default class MobileSearch {
    /**
     * Constructor
     * @param {Object} el - The page main menu as a DOM node.
     * @public
     */
    constructor(el, mobileMenu) {

        /** The mobile search DOM element */
        this.el = el;

        /** The mobileMenu component */
        this.mobileMenu = mobileMenu;

        /** The search button DOM element. */
        this.searchButtonNode = document.querySelector('#search-button');

        /** The search-close button DOM element. */
        this.searchCloseButtonNode = document.querySelector('#search-close-button');

        /** The form DOM node for the search */
        this.formNode = this.el.querySelector('[data-type="mobileSearch"]');

        /** The search input field */
        this.formInput = this.formNode.querySelector('input');

        /** Target id of results container */
        this.resultsTargetId = this.formNode.getAttribute('aria-controls');

        /** Container of search results */
        this.resultsContainer = this.el.querySelector('#' + this.resultsTargetId);

        /** Container of initial text with popular links */
        this.textContainer = this.el.querySelector('.text');

        /** Heading of search results */
        this.resultsHeading = this.el.querySelector('.block-section__subheading');

        /** Loader within the results container */
        this.loader = this.el.querySelector('.loader');

        /** If request is in progress */
        this.requestInProgress = false;

        this.timer = null;

        if (this.searchButtonNode) {
            this._init();
        }
    }

    /**
     * The initialization function for this module.
     * @private
     */
    _init() {
        this.searchButtonNode.addEventListener('click', e => {
            e.preventDefault();
            this._openSearch();
        });

        this.searchCloseButtonNode.addEventListener('click', e => {
            e.preventDefault();
            this._closeSearch();
        });

        this.formInput.addEventListener('input', e => {
            clearTimeout(this.timer);
            this._clearResult();

            if (e.target.value.length > 2) {
                this.textContainer?.classList.add('is-hidden');
                this.timer = setTimeout(() => this._triggerAutocomplete(e.target.value), 900);
            } else {
                this.resultsHeading.classList.add('is-hidden');
                this.textContainer?.classList.remove('is-hidden');
            }
        });

        document.addEventListener('click', e => {
            const link = e.target;

            if (link.classList.contains('mobile-menu__topbar__search')) {
                e.preventDefault();

                const body = document.body;

                if (body.classList.contains('search-open')) {
                    this.searchButtonNode.setAttribute('aria-expanded', false);
                    this._closeSearch();
                } else {
                    this.searchButtonNode.setAttribute('aria-expanded', true);
                    this._openSearch();
                }
            }

        });
    }

    _openSearch() {
        const body = document.body;
        const pageContainer = document.getElementById('page-container');

        this.el.setAttribute('aria-hidden', false);
        body.classList.add('search-open', 'search-is-visible');
        pageContainer?.classList.add('mobile-menu-slideout');

        setTimeout(() => body.classList.add('search-opened'), 100);

        this.mobileMenu.closeMenu();

        this.formInput.focus();

        setTimeout(() => {
            this.formInput.focus();
            this.formInput.select();
        }, 120);
    }

    _closeSearch() {
        const body = document.body;
        const pageContainer = document.getElementById('page-container');

        this.el.setAttribute('aria-hidden', true);
        body.classList.remove('search-open', 'search-opened');

        setTimeout(() => {
            body.classList.remove('search-is-visible');
            pageContainer?.classList.remove('mobile-menu-slideout');
        }, 400);
    }

    /**
     * Triggers autocomplete search
     * @param {String} value - The searched value
     */
    _triggerAutocomplete(value) {
        if (this.requestInProgress) {
            return;
        }

        this.requestInProgress = true;

        const url = this.formNode.dataset.ajaxurl;
        const method = this.formNode.getAttribute('method') || 'GET';
        const name = this.formInput.getAttribute('name') || 'q';
        const lang = this.formNode.querySelector('#lang-mobile');

        this.resultsHeading.classList.remove('is-hidden');
        this.loader.classList.remove('is-hidden');

        axios({
            url: `${url}?${name}=${value}&${lang?.name || 'lang'}=${lang?.value || 'sv'}`,
            method
        })
            .then(response => {
                if (response.status === 200) {
                    if (response.data.items.length > 0) {
                        this._renderResults(response.data);
                    }
                } else {
                    alert('An error occured, try again later.');
                }
            })
            .catch(e => {
                console.log(e);
            })
            .finally(() => {
                this.loader.classList.add('is-hidden');
                this.requestInProgress = false;
            });
    }

    /**
     * Renders results to
     * @param {Object} results - The results from backend
     */
    _renderResults(results) {
        const resultsHtml = resultsTemplate(results);
        this.resultsContainer.insertAdjacentHTML('BeforeEnd', resultsHtml);
    }

    _clearResult() {
        const alist = this.resultsContainer.querySelector('.article-list');
        alist?.remove();
    }
}
